import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { MessageCircle } from 'lucide-react';
import CopyButton from './CopyButton';
import { buildWhatsAppMessage, whatsAppShareUrl } from '../../utils/dietitianInvite';
import { btnPrimary, card, hint, sectionTitle } from './ui';

/**
 * The saved code + client link with Copy, WhatsApp and a QR code.
 * `compact` drops the QR to a smaller size for the Team side panel.
 */
export default function ShareLinkCard({ code, link, t, language, compact = false, dirty = false }) {
  if (!code || !link) {
    return (
      <section className={`${card} p-4`} aria-label={t('share.title')}>
        <h2 className={sectionTitle}>{t('share.title')}</h2>
        <p className={`mt-1 text-[13px] text-ink-muted`}>{t('share.noCode')}</p>
      </section>
    );
  }

  const waUrl = whatsAppShareUrl(buildWhatsAppMessage({ link, code, language }));
  const qrSize = compact ? 112 : 152;

  return (
    <section className={`${card} p-4 sm:p-6`} aria-label={t('share.title')}>
      <div className="flex flex-col sm:flex-row gap-6">
        <div className="min-w-0 flex-1">
          <h2 className={sectionTitle}>{t('share.title')}</h2>

          <p className={`${hint} mt-4`}>{t('share.codeLabel')}</p>
          <p
            className="font-mono text-[36px] leading-none font-semibold tracking-wider text-ink mt-1 break-all"
            dir="ltr"
          >
            {code}
          </p>

          <p className={`${hint} mt-4`}>{t('share.linkLabel')}</p>
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            dir="ltr"
            className="mt-1 block break-all text-[13px] font-medium text-forest-ondark underline-offset-2 hover:underline"
          >
            {link}
          </a>

          <div className="mt-4 flex flex-wrap gap-2">
            <CopyButton text={link} label={t('share.copyLink')} copiedLabel={t('share.copied')} />
            <CopyButton text={code} label={t('share.copyCode')} copiedLabel={t('share.copied')} />
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className={btnPrimary}>
              <MessageCircle size={16} aria-hidden />
              {t('share.whatsapp')}
            </a>
          </div>
          {dirty ? <p className={`${hint} mt-3`}>{t('share.unsaved')}</p> : null}
        </div>

        <figure className="shrink-0 flex flex-col items-center gap-2">
          {/* White tile in both themes so phone cameras read it. */}
          <div className="rounded-lg border border-line bg-white p-3">
            <QRCodeSVG value={link} size={qrSize} level="M" role="img" aria-label={t('share.qrLabel')} />
          </div>
          {!compact ? <figcaption className={`${hint} max-w-[180px] text-center`}>{t('share.qrHint')}</figcaption> : null}
        </figure>
      </div>
    </section>
  );
}
